import React, { useEffect, useState } from "react";
import { FaPlus, FaTimes, FaLock } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import Switch from "react-switch";
import { editEmailType, getStatus } from "../utils/data";
import { BsChevronDown } from "react-icons/bs";
import { useUpdateUserProfile } from "../hooks/updateUserProfile";
import { toast } from "react-toastify";
import "react-toastify/ReactToastify.css";

const EditEmailAddress = ({ userProfile, isLoading, refetch }) => {
  const {
    updateProfile,
    isLoading: isUpdating,
    error,
  } = useUpdateUserProfile();

  const [emailAddresses, setEmailAddresses] = useState([]);
  const [newEmail, setNewEmail] = useState({
    type: "",
    address: "",
    isPreferred: false,
  });
  const [customLabel, setCustomLabel] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [overflowStatus, setOverflowStatus] = useState(false);
  const [emailTypeIcon, setEmailTypeIcon] = useState(false);

  const emailTypeIconRotate = emailTypeIcon ? "rotate(180deg)" : "rotate(0)";

  // Initialize email addresses from userProfile
  useEffect(() => {
    if (userProfile) {
      const emails = [];

      // Primary email from top-level field — always first, read-only
      if (userProfile.email) {
        emails.push({
          id: "primary",
          type: "Primary",
          address: userProfile.email,
          isPreferred: false,
        });
      }

      // Additional emails from the emails array — skip primary (already added above)
      if (userProfile.emails?.length) {
        userProfile.emails
          .filter((e) => e.type?.toLowerCase() !== "primary" && e.value !== userProfile.email)
          .forEach((e) => {
            emails.push({
              id: e.id ?? e.type,
              type: e.type.charAt(0).toUpperCase() + e.type.slice(1).toLowerCase(),
              address: e.value,
              isPreferred: e.preferred,
            });
          });
      }

      setEmailAddresses(emails);
    }
  }, [userProfile]);

  const handleEmailChange = (id, field, value) => {
    setEmailAddresses((prev) =>
      prev.map((email) =>
        email.id === id ? { ...email, [field]: value } : email
      )
    );
  };

  const handlePreferredChange = (id, isPreferred) => {
    setEmailAddresses((prev) =>
      prev.map((email) => ({
        ...email,
        isPreferred: email.id === id ? isPreferred : false,
      }))
    );
  };

  const handleRemoveEmail = (id) => {
    setEmailAddresses((prev) => prev.filter((email) => email.id !== id));
  };

  const handleAddEmail = () => {
    if (!newEmail.address || !newEmail.type) {
      toast.error("Please fill in both email address and type");
      return;
    }

    // Check if email already exists
    const emailExists = emailAddresses.some(
      (email) => email.address === newEmail.address
    );
    if (emailExists) {
      toast.error("This email address already exists");
      return;
    }

    const emailType = newEmail.type === "custom" ? customLabel : newEmail.type;

    const newEmailEntry = {
      id: Date.now().toString(),
      type: emailType,
      address: newEmail.address,
      isPreferred: newEmail.isPreferred,
    };

    // If this is set as preferred, unset others
    if (newEmail.isPreferred) {
      setEmailAddresses((prev) => [
        ...prev.map((email) => ({ ...email, isPreferred: false })),
        newEmailEntry,
      ]);
    } else {
      setEmailAddresses((prev) => [...prev, newEmailEntry]);
    }

    // Reset form
    setNewEmail({ type: "", address: "", isPreferred: false });
    setCustomLabel("");
    setShowAddForm(false);
    setOverflowStatus(false);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (emailAddresses.length === 0) {
      toast.error("At least one email address is required");
      return;
    }

    const updatedData = {
      id: userProfile.id,
      emails: emailAddresses.map((e) => ({
        type: e.type.toLowerCase(),
        value: e.address,
        preferred: e.isPreferred,
      })),
    };

    try {
      await updateProfile(updatedData);

      // Suppress toast on first login — FirstLoginProfileLayout shows its own
      const isFirstLogin = window.location.pathname.includes("first_login_profile");
      if (!isFirstLogin) {
        toast.success("Email addresses updated successfully!");
      }

      // Close modal
      const modal = document.getElementById("editEmailAddress");
      if (modal) {
        window.$(modal).modal("hide");
        // Refetch will be handled by React Query invalidation
        refetch && refetch();
      }
    } catch (error) {
      console.error("Failed to update email addresses:", error);
      toast.error("Failed to update email addresses. Please try again.");
    }
  };

  const handleEmailTypeSelect = (type) => {
    setNewEmail((prev) => ({ ...prev, type }));
  };

  return (
    <div
      className="modal fade"
      id="editEmailAddress"
      tabIndex="-1"
      aria-labelledby="editEmailAddress"
      aria-hidden="true"
    >
      <div className="modal-dialog modal-lg">
        <form onSubmit={handleSubmit}>
          <div className="modal-content border-0 overlay-rounded">
            <div className="modal-header mt-14 border-0 pb-2 mb-1">
              <label className="fs-15" id="editEmailAddress">
                EDIT: Email Address
              </label>

              <FaTimes
                size={15}
                className="cursor-pointer text_primary"
                data-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body py-0">
              <div className="d-flex justify-content-end">
                <p className="text_primary small-text">
                  Select Preferred Email Address
                </p>
              </div>

              {/* Existing Email Addresses */}
              {emailAddresses.map((email, index) => {
                const isPrimary = email.type?.toLowerCase() === "primary";
                return (
                  <div
                    key={email.id}
                    className="d-flex justify-content-between p-0 mb-3"
                  >
                    <div className="d-flex small-text">
                      {!isPrimary && emailAddresses.length > 1 && (
                        <div className="d-flex align-items-center">
                          <MdClose
                            className="text_danger cursor-pointer"
                            size={20}
                            onClick={() => handleRemoveEmail(email.id)}
                          />
                        </div>
                      )}
                      {isPrimary && (
                        <div className="d-flex align-items-center" style={{ width: 20 }}>
                          <FaLock className="text-muted" size={13} title="Primary email cannot be edited" />
                        </div>
                      )}
                      <div className={`form-group row mb-0 ${isPrimary ? "ml-2" : emailAddresses.length > 1 ? "ml-2" : "ml-0"}`}>
                        <label className="col-md-5 col-form-label">
                          {email.type} Email Address:
                          {isPrimary && (
                            <span className="text-muted ml-1" style={{ fontSize: "10px" }}>(read-only)</span>
                          )}
                        </label>
                        <div className="col-md-7">
                          <input
                            type="email"
                            className={`small-text overlay-input${isPrimary ? " bg-light text-muted" : ""}`}
                            style={isPrimary ? { cursor: "not-allowed", opacity: 0.7 } : {}}
                            value={email.address}
                            onChange={(e) =>
                              !isPrimary &&
                              handleEmailChange(email.id, "address", e.target.value)
                            }
                            readOnly={isPrimary}
                            disabled={isUpdating && !isPrimary}
                            required
                          />
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center small-text">
                      {email.isPreferred && (
                        <div className="badge_pomme bg_primary f-10 text-light rounded-pill d-flex align-items-center justify-content-center">
                          <span>Preferred Email Address</span>
                        </div>
                      )}
                      <div className="ml-4">
                        <Switch
                          onChange={(checked) =>
                            !isPrimary && handlePreferredChange(email.id, checked)
                          }
                          checked={email.isPreferred}
                          onColor="#5D8FA8"
                          onHandleColor="#50a193"
                          handleDiameter={20}
                          uncheckedIcon={false}
                          checkedIcon={false}
                          boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                          activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                          height={14}
                          width={35}
                          disabled={isUpdating || isPrimary}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Add Another Email Button */}
              <div className={`mt-4 ${showAddForm ? "d-none" : ""}`}>
                <button
                  type="button"
                  className="btn btn-link text_primary f-11 text-decoration-none"
                  onClick={() => {
                    setShowAddForm(true);
                    setTimeout(() => {
                      setOverflowStatus(true);
                    }, 400);
                  }}
                >
                  <FaPlus size={10} className="mr-1" /> Add another Email
                  Address
                </button>
              </div>

              {/* Add New Email Form */}
              <div
                className={`d-flex justify-content-between p-0 slide-down ${
                  showAddForm ? "showForm" : ""
                } ${overflowStatus ? "overflow-visible" : ""}`}
              >
                <div className="d-flex small-text w-100">
                  <div className="d-flex align-items-center">
                    <MdClose
                      className="text_danger cursor-pointer"
                      size={20}
                      onClick={() => {
                        setShowAddForm(false);
                        setOverflowStatus(false);
                        setNewEmail({
                          type: "",
                          address: "",
                          isPreferred: false,
                        });
                        setCustomLabel("");
                      }}
                    />
                  </div>
                  <div className="form-group row mb-0 ml-2 w-100">
                    <div className="col-md-4">
                      <div className="dropdown">
                        <button
                          className="btn btn-block border text-left overlay-dropdown-input-small"
                          type="button"
                          id="dropdownMenuButton"
                          data-toggle="dropdown"
                          data-display="static"
                          aria-haspopup="true"
                          aria-expanded="false"
                          onClickCapture={() =>
                            setEmailTypeIcon(!emailTypeIcon)
                          }
                        >
                          <div className="d-flex justify-content-between">
                            <small className="small-text">
                              {newEmail.type || "Select type of Email Address"}
                            </small>
                            <BsChevronDown
                              style={{
                                transform: emailTypeIconRotate,
                                transition: "all 0.3s linear",
                              }}
                            />
                          </div>
                        </button>
                        <div
                          className="dropdown-menu dropdown-menu--animate custom border-0 shadow overlay-dropdown-container"
                          aria-labelledby="dropdownMenuButton"
                        >
                          <p className="pl-4 mb-0 pb-2">
                            <small>Choose one type only:</small>
                          </p>
                          {editEmailType.map((item, key) => (
                            <a
                              key={key}
                              onClick={() => handleEmailTypeSelect(item.value)}
                              className="dropdown-item activeHover ml-4 wa"
                              href="#"
                            >
                              <small>{item.label}</small>
                            </a>
                          ))}
                          <div className="ml-5 small-text mb-2">
                            <label className="pt-2">Custom label:</label>
                            <div className="pt-1 d-flex">
                              <input
                                type="text"
                                className="small-text overlay-input"
                                placeholder="Insert custom label"
                                value={customLabel}
                                onChange={(e) => setCustomLabel(e.target.value)}
                              />
                              <button
                                type="button"
                                className="btn btn-outline-primary w-75 h-35 small-text ml-3"
                                onClick={() => {
                                  if (customLabel.trim()) {
                                    setNewEmail((prev) => ({
                                      ...prev,
                                      type: customLabel.trim(),
                                    }));
                                  }
                                }}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <input
                        type="email"
                        className="small-text overlay-input"
                        placeholder="Insert Email Address"
                        value={newEmail.address}
                        onChange={(e) =>
                          setNewEmail((prev) => ({
                            ...prev,
                            address: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="col-md-2 ml-2">
                      <button
                        type="button"
                        className="btn btn-outline-primary w-75 h-35 small-text"
                        onClick={handleAddEmail}
                      >
                        Add
                      </button>
                    </div>
                    <div className="col-md-2 d-flex align-items-center">
                      <Switch
                        onChange={(checked) =>
                          setNewEmail((prev) => ({
                            ...prev,
                            isPreferred: checked,
                          }))
                        }
                        checked={newEmail.isPreferred}
                        onColor="#5D8FA8"
                        onHandleColor="#50a193"
                        handleDiameter={16}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={12}
                        width={30}
                      />
                      <small className="ml-2">Preferred</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 d-flex justify-content-between mt-5">
              <button
                type="button"
                className="btn btn_outline_danger rounded overlay-btn"
                data-dismiss="modal"
              >
                Close
              </button>
              <button
                type="submit"
                className="btn btn-outline-primary rounded overlay-btn"
                disabled={isUpdating}
              >
                {isUpdating ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmailAddress;
